import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import axiosInstance from '../utils/axiosConfig';
import '../styles/EmlakPortfoy.css';
import { useTranslation } from 'react-i18next';
import KayitTakipButonu from '../components/takip/KayitTakipButonu';

interface EmlakFotograf {
  fotoID: number;
  dosyaYolu: string;
  isVitrin: boolean;
}

interface EmlakIletisim {
  iletisimID: number;
  adSoyad: string;
  telefon?: string;
  eposta?: string;
}

interface EmlakOzellik {
  ozellikID: number;
  ozellikAdi: string;
}

interface EmlakPortfoy {
  portfoyID: number;
  baslik: string;
  adres?: string;
  aciklama?: string;
  fiyat: number;
  m2: number;
  ilanNo: string;
  tur: string;
  durum: string;
  eklenmeTarihi: string;
  aktifMi: boolean;
  iletisimler: EmlakIletisim[];
  ozellikler: EmlakOzellik[];
  fotograflar: EmlakFotograf[];
}

const turLabels: Record<string, string> = {
  'Konut': 'Konut', 'IsYeri': 'İş Yeri', 'Arsa': 'Arsa', 'Tarla': 'Tarla'
};
const durumLabels: Record<string, string> = {
  'Satilik': 'Satılık', 'Kiralik': 'Kiralık', 'Portfoy': 'Portföy'
};
const turIcons: Record<string, string> = {
  'Konut': 'fa-home', 'IsYeri': 'fa-briefcase', 'Arsa': 'fa-map', 'Tarla': 'fa-seedling'
};

const EmlakPortfoyDetay: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const [portfoy, setPortfoy] = useState<EmlakPortfoy | null>(null);
  const [loading, setLoading] = useState(true);
  const [activePhoto, setActivePhoto] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [dragStart, setDragStart] = useState<number | null>(null);
  const [dragging, setDragging] = useState(false);

  useEffect(() => {
    if (id) fetchPortfoy(parseInt(id));
  }, [id]);

  const fetchPortfoy = async (portfoyId: number) => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/api/EmlakPortfoy/${portfoyId}`);
      setPortfoy(response.data);
      // Vitrin fotoğrafını aktif yap
      const vitrinIndex = response.data.fotograflar.findIndex((f: EmlakFotograf) => f.isVitrin);
      if (vitrinIndex >= 0) setActivePhoto(vitrinIndex);
    } catch (error) {
      toast.error(t('msg.islemHatasi'));
      navigate('/emlak-portfoy');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!portfoy) return;
    try {
      await axiosInstance.delete(`/api/EmlakPortfoy/${portfoy.portfoyID}`);
      toast.success(t('msg.islemBasarili'));
      navigate('/emlak-portfoy');
    } catch (error) {
      toast.error(t('msg.islemHatasi'));
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    setDragStart(e.clientX);
    setDragging(false);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (dragStart !== null && Math.abs(e.clientX - dragStart) > 10) {
      setDragging(true);
    }
  };

  const handleMouseUp = (e: React.MouseEvent) => {
    if (dragStart !== null && dragging && portfoy) {
      const diff = e.clientX - dragStart;
      if (diff > 50) {
        setActivePhoto(prev => prev === 0 ? portfoy.fotograflar.length - 1 : prev - 1);
      } else if (diff < -50) {
        setActivePhoto(prev => prev === portfoy.fotograflar.length - 1 ? 0 : prev + 1);
      }
    }
    setDragStart(null);
    setDragging(false);
  };

  if (loading) {
    return (
      <div className="ep-container">
        <div className="ep-loading"><i className="fas fa-spinner fa-spin"></i> Yükleniyor...</div>
      </div>
    );
  }

  if (!portfoy) return null;

  const fotoUrl = (dosyaYolu: string) => `${axiosInstance.defaults.baseURL}/api/EmlakPortfoy/foto/${dosyaYolu}`;

  return (
    <div className="ep-container">
      {/* Header */}
      <div className="ep-header">
        <div className="ep-header-left">
          <button className="ep-btn-back" onClick={() => navigate('/emlak-portfoy')}>
            <i className="fas fa-arrow-left"></i>
          </button>
          <h1 className="ep-title">{portfoy.baslik}</h1>
          <span className="ep-count">{portfoy.ilanNo}</span>
        </div>
        <div className="ep-header-actions">
          <KayitTakipButonu tur="EMLAK" anahtar={portfoy.portfoyID} baslik={portfoy.baslik} />
          <button className="ep-btn-primary" onClick={() => navigate(`/emlak-portfoy/duzenle/${portfoy.portfoyID}`)}>
            <i className="fas fa-edit"></i> Düzenle
          </button>
          <button className="ep-btn-danger-outline" onClick={() => setDeleteConfirm(true)}>
            <i className="fas fa-trash"></i> Sil
          </button>
        </div>
      </div>

      <div className="ep-detail-layout">
        {/* Sol: Galeri + Açıklama */}
        <div className="ep-detail-main">
          {/* Galeri */}
          {portfoy.fotograflar.length > 0 ? (
            <div className="ep-gallery">
              <div
                className="ep-gallery-main"
                onClick={() => { if (!dragging) setLightbox(true); }}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={() => { setDragStart(null); setDragging(false); }}
              >
                <img src={fotoUrl(portfoy.fotograflar[activePhoto].dosyaYolu)} alt={portfoy.baslik} draggable={false} />
                <div className="ep-gallery-zoom-hint">
                  <i className="fas fa-search-plus"></i>
                </div>
                {portfoy.fotograflar.length > 1 && (
                  <>
                    <button className="ep-gallery-nav ep-gallery-nav-prev" onClick={e => {
                      e.stopPropagation();
                      setActivePhoto(prev => prev === 0 ? portfoy.fotograflar.length - 1 : prev - 1);
                    }}>
                      <i className="fas fa-chevron-left"></i>
                    </button>
                    <button className="ep-gallery-nav ep-gallery-nav-next" onClick={e => {
                      e.stopPropagation();
                      setActivePhoto(prev => prev === portfoy.fotograflar.length - 1 ? 0 : prev + 1);
                    }}>
                      <i className="fas fa-chevron-right"></i>
                    </button>
                  </>
                )}
              </div>
              {portfoy.fotograflar.length > 1 && (
                <div className="ep-gallery-thumbs">
                  {portfoy.fotograflar.map((foto, idx) => (
                    <div
                      key={foto.fotoID}
                      className={`ep-gallery-thumb ${idx === activePhoto ? 'active' : ''}`}
                      onClick={() => setActivePhoto(idx)}
                    >
                      <img src={fotoUrl(foto.dosyaYolu)} alt="" />
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="ep-no-gallery">
              <i className="fas fa-image"></i>
              <p>Fotoğraf eklenmemiş</p>
            </div>
          )}

          {/* Açıklama */}
          {portfoy.aciklama && (
            <div className="ep-detail-section">
              <h2 className="ep-detail-section-title"><i className="fas fa-align-left"></i> Açıklama</h2>
              <div className="ep-detail-aciklama" dangerouslySetInnerHTML={{ __html: portfoy.aciklama }} />
            </div>
          )}
        </div>

        {/* Sağ: Bilgi paneli */}
        <div className="ep-detail-sidebar">
          {/* Fiyat kartı */}
          <div className="ep-price-card">
            <div className="ep-price-value">{portfoy.fiyat.toLocaleString('tr-TR')} ₺</div>
            <div className="ep-price-m2">{Math.round(portfoy.fiyat / portfoy.m2).toLocaleString('tr-TR')} ₺/m²</div>
          </div>

          {/* Detaylar */}
          <div className="ep-detail-section">
            <h2 className="ep-detail-section-title"><i className="fas fa-list-ul"></i> Detaylar</h2>
            <div className="ep-info-list">
              <div className="ep-info-item">
                <span className="ep-info-label"><i className={`fas ${turIcons[portfoy.tur] || 'fa-home'}`}></i> Tür</span>
                <span className="ep-info-value">{turLabels[portfoy.tur] || portfoy.tur}</span>
              </div>
              <div className="ep-info-item">
                <span className="ep-info-label"><i className="fas fa-tag"></i> Durum</span>
                <span className={`ep-badge ep-badge-${portfoy.durum.toLowerCase()}`}>{durumLabels[portfoy.durum] || portfoy.durum}</span>
              </div>
              <div className="ep-info-item">
                <span className="ep-info-label"><i className="fas fa-ruler-combined"></i> Alan</span>
                <span className="ep-info-value">{portfoy.m2} m²</span>
              </div>
              {portfoy.adres && (
                <div className="ep-info-item">
                  <span className="ep-info-label"><i className="fas fa-map-marker-alt"></i> Adres</span>
                  <span className="ep-info-value">{portfoy.adres}</span>
                </div>
              )}
              <div className="ep-info-item">
                <span className="ep-info-label"><i className="fas fa-hashtag"></i> İlan No</span>
                <span className="ep-info-value">{portfoy.ilanNo}</span>
              </div>
              <div className="ep-info-item">
                <span className="ep-info-label"><i className="fas fa-calendar"></i> Eklenme</span>
                <span className="ep-info-value">{formatDate(portfoy.eklenmeTarihi)}</span>
              </div>
            </div>
          </div>

          {/* Özellikler */}
          {portfoy.ozellikler.length > 0 && (
            <div className="ep-detail-section">
              <h2 className="ep-detail-section-title"><i className="fas fa-tags"></i> Özellikler</h2>
              <div className="ep-ozellik-tags">
                {portfoy.ozellikler.map(oz => (
                  <span key={oz.ozellikID} className="ep-ozellik-tag ep-ozellik-tag-detail">
                    <i className="fas fa-check"></i> {oz.ozellikAdi}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* İlgili Kişiler */}
          {portfoy.iletisimler.length > 0 && (
            <div className="ep-detail-section">
              <h2 className="ep-detail-section-title"><i className="fas fa-address-book"></i> İlgili Kişiler</h2>
              {portfoy.iletisimler.map(kisi => (
                <div key={kisi.iletisimID} className="ep-kisi-card">
                  <div className="ep-kisi-avatar">
                    <i className="fas fa-user"></i>
                  </div>
                  <div className="ep-kisi-info">
                    <div className="ep-kisi-name">{kisi.adSoyad}</div>
                    {kisi.telefon && (
                      <a href={`tel:${kisi.telefon}`} className="ep-kisi-detail">
                        <i className="fas fa-phone"></i> {kisi.telefon}
                      </a>
                    )}
                    {kisi.eposta && (
                      <a href={`mailto:${kisi.eposta}`} className="ep-kisi-detail">
                        <i className="fas fa-envelope"></i> {kisi.eposta}
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Lightbox */}
      {lightbox && portfoy.fotograflar.length > 0 && (
        <div className="ep-lightbox" onClick={() => setLightbox(false)}>
          <button className="ep-lightbox-close" onClick={() => setLightbox(false)}>
            <i className="fas fa-times"></i>
          </button>
          {portfoy.fotograflar.length > 1 && (
            <>
              <button className="ep-lightbox-prev" onClick={e => { e.stopPropagation(); setActivePhoto(prev => prev === 0 ? portfoy.fotograflar.length - 1 : prev - 1); }}>
                <i className="fas fa-chevron-left"></i>
              </button>
              <button className="ep-lightbox-next" onClick={e => { e.stopPropagation(); setActivePhoto(prev => prev === portfoy.fotograflar.length - 1 ? 0 : prev + 1); }}>
                <i className="fas fa-chevron-right"></i>
              </button>
            </>
          )}
          <img src={fotoUrl(portfoy.fotograflar[activePhoto].dosyaYolu)} alt="" onClick={e => e.stopPropagation()} />
          <div className="ep-lightbox-counter">{activePhoto + 1} / {portfoy.fotograflar.length}</div>
        </div>
      )}
      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="ep-modal-overlay" onClick={() => setDeleteConfirm(false)}>
          <div className="ep-modal" onClick={e => e.stopPropagation()}>
            <div className="ep-modal-header">
              <span className="ep-modal-title"><i className="fas fa-exclamation-triangle" style={{ color: '#dc2626', marginRight: 8 }}></i>İlanı Sil</span>
              <button className="ep-modal-close" onClick={() => setDeleteConfirm(false)}>✕</button>
            </div>
            <div className="ep-modal-body">
              <p>Bu ilanı silmek istediğinize emin misiniz? Bu işlem geri alınamaz.</p>
            </div>
            <div className="ep-modal-footer">
              <button className="ep-btn-cancel" onClick={() => setDeleteConfirm(false)}>{t('btn.iptal')}</button>
              <button className="ep-btn-danger" onClick={handleDelete}>
                <i className="fas fa-trash"></i> Sil
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmlakPortfoyDetay;
